import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Moon, Search, Sun } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuthStore } from "@/store/authStore";
import { useUiStore } from "@/store/uiStore";

export function Header() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const darkMode = useUiStore((s) => s.darkMode);
  const setDarkMode = useUiStore((s) => s.setDarkMode);
  const t = useUiStore((s) => s.t);
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  function onSearch(e: FormEvent) {
    e.preventDefault();
    const next = q.trim();
    if (next.length < 2) return;
    navigate(`/search?q=${encodeURIComponent(next)}`);
  }

  return (
    <header className="flex h-14 items-center gap-3 border-b border-zinc-200 bg-white px-4 dark:border-zinc-800 dark:bg-zinc-950">
      <Button variant="ghost" size="sm" onClick={toggleSidebar} aria-label={t("app.toggleSidebar")}>
        <Menu className="h-4 w-4" />
      </Button>

      <form onSubmit={onSearch} className="relative hidden max-w-md flex-1 md:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("app.searchPlaceholder")}
          aria-label={t("app.searchPlaceholder")}
          className="h-9 w-full rounded-md border border-zinc-200 bg-zinc-50 pl-9 pr-3 text-sm outline-none focus:border-[var(--amos-brand)] focus:ring-2 focus:ring-[color:var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </form>

      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden"
          onClick={() => navigate("/search")}
          aria-label={t("pages.search.title")}
        >
          <Search className="h-4 w-4" />
        </Button>
        <NotificationBell />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDarkMode(!darkMode)}
          aria-label={t("app.toggleDark")}
        >
          {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <div className="hidden text-right sm:block">
          <div className="text-xs font-medium text-zinc-900 dark:text-zinc-50">{user?.name}</div>
          <div className="text-[10px] text-zinc-500">{user?.email}</div>
        </div>
        <Button variant="outline" size="sm" onClick={logout}>
          {t("app.signOut")}
        </Button>
      </div>
    </header>
  );
}
